package app.nestbridge.feature.profile.presentation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.produceState
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import app.nestbridge.app.context.identity.UserContext
import app.nestbridge.app.usecase.GetUserContextUseCase
import app.nestbridge.app.usecase.ReadAuthSessionUseCase
import app.nestbridge.app.usecase.SetUserContextUseCase
import app.nestbridge.domain.identity.model.ProfessionalProfile
import app.nestbridge.domain.identity.model.ProfessionalServiceType
import app.nestbridge.domain.identity.usecase.SaveProfessionalProfileUseCase
import app.nestbridge.feature.careteam.screen.CreateCareTeamScreen
import app.nestbridge.feature.profile.screen.CreateProfessionalProfileEvent
import app.nestbridge.feature.profile.screen.CreateProfessionalProfileResult
import app.nestbridge.feature.profile.screen.CreateProfessionalProfileScreen
import app.nestbridge.feature.profile.screen.CreateProfessionalProfileState
import com.slack.circuit.codegen.annotations.CircuitInject
import com.slack.circuit.runtime.Navigator
import com.slack.circuit.runtime.presenter.Presenter
import kotlinx.coroutines.launch
import me.tatarka.inject.annotations.Assisted
import me.tatarka.inject.annotations.Inject
import software.amazon.lastmile.kotlin.inject.anvil.AppScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlin.time.Clock.System

@CircuitInject(
    screen = CreateProfessionalProfileScreen::class,
    scope = AppScope::class,
)
@Inject
class CreateProfessionalProfilePresenter(
    @Assisted private val screen: CreateProfessionalProfileScreen,
    @Assisted private val navigator: Navigator,
    private val getUserContextUseCase: GetUserContextUseCase,
    private val setUserContextUseCase: SetUserContextUseCase,
    private val readAuthSessionUseCase: ReadAuthSessionUseCase,
    private val saveProfessionalProfileUseCase: SaveProfessionalProfileUseCase,
) : Presenter<CreateProfessionalProfileState> {

    @Composable
    override fun present(): CreateProfessionalProfileState {
        val scope = rememberCoroutineScope()
        val userContextFlow by produceState<StateFlow<UserContext?>>(initialValue = MutableStateFlow<UserContext?>(null)) {
            value = getUserContextUseCase()
        }
        val userContext by userContextFlow.collectAsState()

        var displayName by remember { mutableStateOf("") }
        var credentials by remember { mutableStateOf("") }
        var practiceName by remember { mutableStateOf("") }
        var serviceType by remember { mutableStateOf<ProfessionalServiceType?>(null) }
        var isSaving by remember { mutableStateOf(false) }
        var errorMessage by remember { mutableStateOf<String?>(null) }

        fun onEvent(event: CreateProfessionalProfileEvent) {
            when (event) {
                CreateProfessionalProfileEvent.Back -> navigator.pop(
                    CreateProfessionalProfileResult(CreateProfessionalProfileResult.Status.Cancelled)
                )
                CreateProfessionalProfileEvent.Continue -> {
                    if (isSaving || serviceType == null) return

                    scope.launch {
                        isSaving = true
                        errorMessage = null

                        try {
                            val context = userContext ?: run {
                                errorMessage = "User context is still loading. Please try again."
                                return@launch
                            }
                            // Get user ID from context
                            val userId = context.user.id
                            // Get session for audit fields
                            val session = readAuthSessionUseCase()
                                ?: throw IllegalStateException("No auth session found")

                            // Parse credentials from comma-separated string
                            val credentialsList = credentials.split(",")
                                .map { it.trim() }
                                .filter { it.isNotBlank() }

                            // Create professional profile
                            val now = System.now()
                            val professionalProfile = ProfessionalProfile(
                                id = "",
                                userId = userId,
                                displayName = displayName,
                                credentials = credentialsList,
                                affiliation = practiceName,
                                primaryServiceProvided = serviceType ?: ProfessionalServiceType.Other,
                                createdAt = now,
                                createdBy = session.userId,
                                modifiedAt = now,
                                modifiedBy = session.userId,
                            )

                            // Save profile
                            val savedProfessionalProfile = saveProfessionalProfileUseCase(professionalProfile)

                            setUserContextUseCase(
                                context.copy(professionalProfile = savedProfessionalProfile),
                            )

                            // Pop with result and navigate to create team screen if childId provided
                            navigator.pop(CreateProfessionalProfileResult(CreateProfessionalProfileResult.Status.Created))
                        } catch (e: Exception) {
                            errorMessage = "We couldn't save your profile. Please try again."
                        } finally {
                            isSaving = false
                        }
                    }
                }
                is CreateProfessionalProfileEvent.UpdateDisplayName -> displayName = event.value
                is CreateProfessionalProfileEvent.UpdateCredentials -> credentials = event.value
                is CreateProfessionalProfileEvent.UpdatePracticeName -> practiceName = event.value
                is CreateProfessionalProfileEvent.UpdateServiceType -> serviceType = event.value
            }
        }

        return CreateProfessionalProfileState(
            displayName = displayName,
            credentials = credentials,
            practiceName = practiceName,
            serviceType = serviceType,
            isSaving = isSaving,
            errorMessage = errorMessage,
            eventSink = ::onEvent,
        )
    }
}
